"use client";

import { useEffect, useState } from "react";
import { Switch } from "@/shadcnComponents/ui/switch";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Briefcase,
  BarChart2,
  Users,
  Copy,
  ExternalLink,
  CheckCircle,
  UserCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shadcnComponents/ui/dialog";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import { useAuth } from "@/Providers/Auth";
import { useInfluencer } from "@/Providers/Influencer";
import MobileMenuBar from "@/Components/Menubar/MobileMenuBar";
import { OpenInNewWindow } from "@/Helpers/helper";
import { useNavigate } from "react-router-dom";

export default function InfluencerActivationSuccessPage() {
  const { toast } = useToast();
  const [openForCollaboration, setOpenForCollaboration] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [, setCopied] = useState(false);
  const authState = useAuth();
  const influencerState = useInfluencer();

  useEffect(() => {
    setOpenForCollaboration(authState?.loggedUser?.openForCollaboration);
  }, [authState]);

  const handleToggleChange = async (checked: boolean) => {
    if (!checked) {
      // Only show confirmation when turning off

      setShowConfirmDialog(true);
    } else {
      setOpenForCollaboration(true);
      await influencerState.updatePersonalInfo({ openForCollaboration: true });
      toast({
        title: "Your profile is now visible to brands",
      });
    }
  };

  const handleConfirmToggleOff = async () => {
    setOpenForCollaboration(false);
    await influencerState.updatePersonalInfo({ openForCollaboration: false });
    setShowConfirmDialog(false);
    toast({
      title: "Your profile is now hidden from brands",
    });
  };

  const copyMediaKitLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/${authState?.loggedUser?.slug}`
    );
    setCopied(true);
    toast({
      title: "Link copied to clipboard",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <MobileMenuBar />
      <div className=" mb-10 md:mb-0 bg-[#1C1C1C] text-white p-8 flex items-center justify-center">
        <div className="max-w-xl w-full space-y-8">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <h1 className="text-2xl font-medium text-red-500">
              Your Profile is active now!
            </h1>
            <p className="text-gray-400 text-sm">
              Now your profile will be visible to brands for brand
              collaboration, and you are eligible to check these benefit
            </p>
          </div>

          <div className="space-y-4">
            <FeatureCard
              icon={Briefcase}
              bgColor="bg-[#E9967A]"
              title="Public Media Kit"
              description="Your professional portfolio, accessible anywhere"
              link={`${window.location.origin}/${authState?.loggedUser?.slug}`}
              onCopy={copyMediaKitLink}
            />

            <FeatureCard
              icon={Users}
              bgColor="bg-[#E9967A]"
              title="Invite-Only Community"
              description="Connect with top-tier creators"
              link="https://go.anchors.in/anchors_WA"
              external
            />

            <FeatureCard
              icon={BarChart2}
              bgColor="bg-[#E9967A]"
              title="Profile Analysis"
              description="Deep insights into your content performance"
              linkText="check LinkedIn post analysis"
              navigateTo="/influencer/posts-performance"
            />

            <div className="bg-[#1E1E1E] rounded-lg p-4 border border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-[#9370DB] rounded-lg flex items-center justify-center">
                    <UserCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-gray-200 text-sm font-medium">
                      open for collaborations
                    </h3>
                    <p className="text-gray-400 text-xs">
                      Let brands discover and work with you
                    </p>
                  </div>
                </div>
                <Switch
                  checked={openForCollaboration}
                  onCheckedChange={handleToggleChange}
                />
              </div>
            </div>
          </div>
        </div>

        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="bg-[#1E1E1E] border-gray-800">
            <DialogHeader>
              <DialogTitle className="text-xl text-white font-semibold">
                Hide Profile from Brands?
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Are you sure you want to hide your profile? Brands won't be able
                to discover you for collaborations while your profile is hidden.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirmDialog(false)}
                className="border-gray-700 hover:bg-gray-800"
              >
                No, keep visible
              </Button>
              <Button variant="destructive" onClick={handleConfirmToggleOff}>
                Yes, hide profile
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

function FeatureCard({
  icon: Icon,
  bgColor,
  title,
  description,
  link,
  linkText,
  onCopy,
  external,
  navigateTo,
}: {
  icon: React.ElementType;
  bgColor: string;
  title: string;
  description: string;
  link?: string | null;
  linkText?: string | null;
  onCopy?: () => void;
  external?: boolean;
  navigateTo?: string;
}) {
  const navigate = useNavigate();
  return (
    <div className="bg-[#1E1E1E] rounded-lg p-4 border border-gray-800">
      <div className="flex items-center space-x-4">
        <div
          className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-gray-200 text-sm font-medium">{title}</h3>
          <p className="text-gray-400 text-xs">{description}</p>
          {link && (
            <div className="mt-2 flex items-center justify-between bg-[#1C1C1C] rounded px-3 py-2">
              <span className="text-red-500 text-xs truncate mr-2">{link}</span>
              <div className="flex items-center space-x-2">
                {onCopy && (
                  <button
                    onClick={onCopy}
                    className="text-gray-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
                {external && (
                  <button
                    onClick={() => OpenInNewWindow({ link })}
                    className="text-gray-400 hover:text-white"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}
          {linkText && (
            <button
              onClick={() => {
                if (navigateTo) {
                  navigate(navigateTo);
                }
              }}
              className="mt-2 text-red-500 text-xs hover:underline"
            >
              {linkText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
